import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft, Check } from 'lucide-react';
import { Button } from './ui/button';

const OnboardingTour = ({ user, isProfileComplete }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState(null);

  const steps = [
    {
      target: '[data-testid="menu-link"]',
      title: 'Explora el Menú',
      content: 'Aquí encontrarás todos nuestros productos organizados por categorías: desayunos, comidas, bebidas y más.',
      position: 'bottom'
    },
    {
      target: '[data-testid="cart-button"]',
      title: 'Tu Carrito',
      content: 'Los productos que agregues aparecerán aquí. Haz clic para revisar tu pedido y proceder al pago.',
      position: 'bottom'
    },
    {
      target: '[data-testid="nutrition-chat-button"]',
      title: 'Tu Asistente Personal',
      content: '¡Esta es tu nutrióloga AI! Puede recomendarte comidas, agregar productos al carrito y crear pedidos recurrentes.',
      position: 'left'
    },
    {
      target: '[data-testid="user-menu"]',
      title: 'Tu Cuenta',
      content: 'Desde aquí accedes a tu perfil, pedidos recurrentes, facturación y métodos de pago.',
      position: 'bottom'
    },
    {
      target: '[data-testid="subscriptions-link"]',
      title: 'Planes de Suscripción',
      content: 'Ahorra con nuestros planes mensuales. Desde 20 hasta comidas ilimitadas al mes.',
      position: 'bottom'
    }
  ];

  // Check if user has completed the tour
  useEffect(() => {
    if (user && isProfileComplete) {
      const tourCompleted = localStorage.getItem(`tour_completed_${user.user_id}`);
      if (!tourCompleted) {
        const timer = setTimeout(() => setIsVisible(true), 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [user, isProfileComplete]);

  // Update target position when step changes
  useEffect(() => {
    if (!isVisible) return;
    
    const updatePosition = () => {
      const step = steps[currentStep];
      const targetEl = document.querySelector(step.target);
      if (targetEl) {
        const rect = targetEl.getBoundingClientRect();
        setTargetRect(rect);
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);
    
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }, [currentStep, isVisible]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeTour();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    completeTour();
  };

  const completeTour = () => {
    setIsVisible(false);
    if (user) {
      localStorage.setItem(`tour_completed_${user.user_id}`, 'true');
    }
  };

  if (!isVisible || !user || !isProfileComplete) return null;

  const currentStepData = steps[currentStep];
  
  // Calculate tooltip position
  const getTooltipStyle = () => {
    if (!targetRect) return { opacity: 0 };

    const padding = 16;
    const tooltipWidth = 320;
    const tooltipHeight = 180;

    let top, left;

    switch (currentStepData.position) {
      case 'bottom':
        top = targetRect.bottom + padding;
        left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
        break;
      case 'top':
        top = targetRect.top - tooltipHeight - padding;
        left = targetRect.left + (targetRect.width / 2) - (tooltipWidth / 2);
        break;
      case 'left':
        top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
        left = targetRect.left - tooltipWidth - padding;
        break;
      case 'right':
        top = targetRect.top + (targetRect.height / 2) - (tooltipHeight / 2);
        left = targetRect.right + padding;
        break;
      default:
        top = targetRect.bottom + padding;
        left = targetRect.left;
    }

    // Keep within viewport
    left = Math.max(16, Math.min(left, window.innerWidth - tooltipWidth - 16));
    top = Math.max(16, Math.min(top, window.innerHeight - tooltipHeight - 16));

    return { top, left, width: tooltipWidth };
  };

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/50 z-[9998] transition-opacity duration-300"
        onClick={handleSkip}
      />

      {/* Spotlight on target */}
      {targetRect && (
        <div
          className="fixed z-[9999] rounded-lg transition-all duration-300 pointer-events-none"
          style={{
            top: targetRect.top - 4,
            left: targetRect.left - 4,
            width: targetRect.width + 8,
            height: targetRect.height + 8,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.5), 0 0 20px rgba(5, 150, 105, 0.5)',
            border: '2px solid #059669'
          }}
        />
      )}

      {/* Tooltip */}
      <div
        className="fixed z-[10000] bg-white rounded-2xl shadow-2xl p-5 transition-all duration-300"
        style={getTooltipStyle()}
      >
        {/* Close button */}
        <button
          onClick={handleSkip}
          className="absolute top-3 right-3 text-stone-400 hover:text-stone-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Progress */}
        <div className="flex gap-1 mb-4">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 rounded-full transition-colors ${
                i <= currentStep ? 'bg-emerald-600' : 'bg-stone-200'
              }`}
            />
          ))}
        </div>

        {/* Content */}
        <h3 className="font-bold text-lg text-stone-900 mb-2">
          {currentStepData.title}
        </h3>
        <p className="text-stone-600 text-sm mb-4">
          {currentStepData.content}
        </p>

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-sm text-stone-500 hover:text-stone-700 transition-colors"
          >
            Saltar tour
          </button>
          <div className="flex gap-2">
            {currentStep > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrev}
                className="rounded-lg"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Anterior
              </Button>
            )}
            <Button
              size="sm"
              onClick={handleNext}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-lg"
            >
              {currentStep === steps.length - 1 ? (
                <>
                  <Check className="w-4 h-4 mr-1" />
                  ¡Listo!
                </>
              ) : (
                <>
                  Siguiente
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingTour;
